import { useCallback, useRef, useState } from 'react';

import { IconButton as ButtonIcon, styled } from '@material-ui/core';

import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../../hooks';
import { blockIdAttribute, generateBlockId } from '../../utils';
import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from '../editorStyles';
import { TagIconPicker } from './TagIconPicker';
import {
  DEFAULT_TAG_ICON,
  TAG_ICON_COMPONENTS,
  type TagIconName,
} from './constants';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tag: {
      setTag: (options?: { icon?: TagIconName }) => ReturnType;
    };
  }
}

const StyledTag = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  paddingRight: 'var(--bui-space-2)',
  backgroundColor: 'var(--bui-bg-neutral-2)',
  borderRadius: 'var(--bui-radius-2)',
  fontSize: 'var(--bui-font-size-2)',
  fontWeight: 500,
  color: 'var(--bui-fg-primary)',
  border: '1px solid var(--bui-border)',
  cursor: 'default',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,

  '&:hover': {
    backgroundColor: 'var(--bui-bg-surface-1)',
  },
});

const StyledContent = styled(NodeViewContent)({
  display: 'inline',
  outline: 'none',
  minWidth: '1ch',
  paddingRight: 2,
});

const PLACEHOLDER_TEXT = 'Add tag text...';

const TagComponent = ({ node, updateAttributes }: NodeViewProps) => {
  const [showIconPicker, setShowIconPicker] = useState(false);
  const iconButtonRef = useRef<HTMLSpanElement>(null);

  const { blockId } = node.attrs;
  const zoneId = useBlockZoneId('tag', blockId, updateAttributes);
  const iconName = (node.attrs.icon as TagIconName) || DEFAULT_TAG_ICON;
  const IconComponent = TAG_ICON_COMPONENTS[iconName];
  const isPlaceholder = node.textContent === PLACEHOLDER_TEXT;

  const handleIconClick = useCallback(() => {
    setShowIconPicker(true);
  }, []);

  const handleIconSelect = useCallback(
    (icon: TagIconName) => {
      updateAttributes({ icon });
      setShowIconPicker(false);
    },
    [updateAttributes],
  );

  const handleClose = useCallback(() => {
    setShowIconPicker(false);
  }, []);

  const content = (
    <>
      <StyledTag>
        <span
          ref={iconButtonRef}
          contentEditable={false}
          style={{ lineHeight: 0 }}
        >
          <ButtonIcon
            size="small"
            aria-label="Change icon"
            onClick={handleIconClick}
          >
            <IconComponent size={14} />
          </ButtonIcon>
        </span>
        <StyledContent
          style={{
            color: isPlaceholder ? 'var(--bui-fg-secondary)' : undefined,
          }}
        />
      </StyledTag>
      {showIconPicker && iconButtonRef.current && (
        <TagIconPicker
          anchorEl={iconButtonRef.current}
          onSelect={handleIconSelect}
          onClose={handleClose}
          currentIcon={iconName}
        />
      )}
    </>
  );

  return (
    <NodeViewWrapper as="span">
      {zoneId ? (
        <CommentZone id={zoneId} component="span">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const Tag = Node.create({
  name: 'tag',
  group: 'block',
  content: 'inline*',

  addAttributes() {
    return {
      icon: {
        default: DEFAULT_TAG_ICON,
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => ({
          'data-icon': attributes.icon,
        }),
      },
      blockId: blockIdAttribute,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="tag"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'tag' }), 0];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(TagComponent);
  },

  addCommands() {
    return {
      setTag:
        (options = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              icon: options.icon || DEFAULT_TAG_ICON,
              blockId: generateBlockId(),
            },
            content: [{ type: 'text', text: PLACEHOLDER_TEXT }],
          });
        },
    };
  },
});
